/**
 * ÜBUNGSVORLAGEN — die reine Logik, ohne Datenbank und ohne Browser
 *
 * Plan: `docs/lernzentrum-tutorial-plan.md`, Abschnitt 8.3 („Vorlagen =
 * Pipeline-Seed"): Der Knopf „Selbst ausprobieren" im Client und das
 * Seed-Skript der Tutorial-Pipeline (`scripts/tutorial/seed-demo.ts`) lesen
 * DIESELBEN Vorlagendateien — sonst zeigen Video, Screenshot und
 * Übungsprojekt verschiedene Stände.
 *
 * ─── Warum diese Datei getrennt von `uebungService.ts` steht ───────────────
 *
 * `uebungService.ts` importiert `../supabaseClient` und lädt die Vorlagen per
 * `import('…json')` — beides Wege, die es nur im Vite-Bundle gibt. Ein
 * Node-Skript, das die Datei importierte, zöge den halben Client mit und
 * scheiterte am fehlenden `import.meta.env`. Hier steht deshalb ausschließlich
 * das, was BEIDE Seiten brauchen: der Vertrag der Vorlagendatei und die
 * Ersetzung der Platzhalter-ids.
 *
 * `uebungService.ts` reicht beides unverändert weiter (`export … from`), damit
 * kein Aufrufer und kein Test umgeschrieben werden muss. Der Vertrag ist
 * derselbe geblieben; nur der Wohnort hat sich geändert.
 */

import type { Report } from '../types';

/**
 * Aufbau einer Vorlagendatei.
 *
 * ─── `report` ist ein ganz normaler `Report` ───────────────────────────────
 *
 * Kein eigenes Vorlagenformat: was in `report` steht, ist exakt das, was die
 * App im Speicher hat, wenn ein Projekt offen ist — mit `items` (Mängel),
 * `metadata.distributionList` (Beteiligte) und
 * `metadata.globalData.services` (das LV). Genau diese Form erwartet die RPC
 * `uebungsprojekt_anlegen` als `p_inhalt` (sie legt daraus `projects.data`
 * an), und genau diese Form schreibt `saveProjectToCloud` in die
 * Kindtabellen. Ein drittes Format dazwischen wäre eine weitere Stelle, an
 * der die Vorlage still veralten könnte.
 *
 * ─── Was die RPC NICHT tut ─────────────────────────────────────────────────
 *
 * Nr. 101 schreibt die Projektzeile und räumt beim Zurücksetzen die
 * Kindzeilen ab — mehr nicht. LV, Beteiligte und Mängel schreibt deshalb der
 * Client anschließend über `saveProjectToCloud`, also über denselben Weg wie
 * jede echte Eingabe. Das ist der Grund, warum ein Übungsprojekt hinterher
 * garantiert ladbar ist: es entsteht auf dem Weg, den `loadProjectById` liest.
 * Die abgeschlossenen Begehungen (`archiv`) gehen über `saveReportSnapshot`
 * nach `report_history`, der SiGe-Plan in seine eigenen Tabellen.
 *
 * ─── Die ids sind PLATZHALTER ──────────────────────────────────────────────
 *
 * Alle `id`-Werte der Vorlage sind feste Platzhalter-UUIDs und stehen
 * vollständig in `platzhalter_ids`. Vor dem Schreiben ersetzt
 * `mitFrischenIds` jede durch eine frische UUID — sonst kollidierte der
 * zweite Nutzer, der dieselbe Vorlage anlegt, mit dem Primärschlüssel des
 * ersten. Ersetzt wird auf dem JSON-TEXT, damit Verweise (Abschnitts-ids,
 * `sige_projekt_id`) mitwandern.
 *
 * ─── Zähler und Journal starten bei NULL ───────────────────────────────────
 *
 * `quantityUsed` ist überall 0 und `globalData.serviceLog` ist leer — auch
 * dort, wo das Archiv schon zwei abgeschlossene Begehungen führt. Das ist
 * Absicht und der eigentliche Übungszweck: der Nutzer bucht selbst. Eine
 * Vorlage mit vorgebuchten Zeilen träfe außerdem genau die Riegel, die die
 * Datenbank gegen echte Vorfälle trägt — Nr. 73/74 (Doppelbuchung je
 * `serviceId`+`reportRef`) und Nr. 77/78 (`quantity_used` darf nicht unter die
 * Protokollsumme fallen). Beim Zurücksetzen liefe der Zähler dann gegen den
 * Trigger und die Vorlage ließe sich nicht wiederherstellen.
 *
 * ─── Keine Vertreter ───────────────────────────────────────────────────────
 *
 * Die Vorlagen führen bewusst KEINE `representatives` und keine
 * Vertreterfelder: die Kindtabellen hängen an `can_access_project`, und ein
 * Vertreter in der Vorlage öffnete fremde Zugriffe auf ein persönliches
 * Übungsprojekt (Vorgabe aus Nr. 100/101).
 */
export interface UebungsVorlagenInhalt {
    vorlage: string;
    version: number;
    /**
     * Der Platzhalter, der überall dort steht, wo die Projekt-id erwartet
     * wird (`report.metadata.globalData.projectId`). Er wird durch die id des
     * angelegten Projekts ersetzt — nicht durch eine zufällige neue.
     */
    projekt_id_platzhalter: string;
    /** Jede id, die vor dem Schreiben neu vergeben werden muss. */
    platzhalter_ids: string[];
    /** Der offene Stand des Projekts — `p_inhalt` der RPC. */
    report: Report;
    /** Abgeschlossene Begehungen; je Eintrag ein ganzer Bericht für `report_history`. */
    archiv: Report[];
    /** Nur Vorlage `sigeplan`: `sige_projekte` + `sige_arbeitsschritte` + `sige_bloecke`. */
    sigeplan?: {
        projekt: Record<string, unknown>;
        arbeitsschritte: Record<string, unknown>[];
        bloecke: Record<string, unknown>[];
    };
}

/**
 * Jede Platzhalter-id durch eine frische UUID ersetzen.
 *
 * Textersetzung auf dem serialisierten JSON, nicht Feld für Feld: eine id
 * steht in der Vorlage an mehreren Stellen (der Mangel in `items`, der
 * Arbeitsschritt unter `sige_projekt_id`), und ein Feldbesuch müsste jede
 * dieser Stellen kennen. Die Platzhalter sind vollständige UUIDs und kommen
 * in keinem Fließtext vor — eine Verwechslung ist ausgeschlossen.
 *
 * `projekt_id_platzhalter` bleibt ausgespart: den setzt der Aufrufer auf die
 * id, die die RPC vergeben hat.
 *
 * `neueId` ist einspeisbar, und das ist mehr als eine Testhilfe: das
 * Seed-Skript der Pipeline (Paket C) reicht dort einen ABLEITENDEN Erzeuger
 * herein, damit derselbe Lauf zweimal dieselben ids schreibt. Ein
 * Demo-Projekt, dessen Kindzeilen bei jedem nächtlichen Lauf neue
 * Primärschlüssel bekämen, erzwänge sonst bei jedem Lauf neue Screenshots.
 */
export const mitFrischenIds = (
    inhalt: UebungsVorlagenInhalt,
    neueId: () => string = () => crypto.randomUUID(),
): UebungsVorlagenInhalt => {
    let text = JSON.stringify(inhalt);
    for (const platzhalter of inhalt.platzhalter_ids) {
        if (platzhalter === inhalt.projekt_id_platzhalter) continue;
        text = text.split(platzhalter).join(neueId());
    }
    return JSON.parse(text) as UebungsVorlagenInhalt;
};
